/* eslint-disable no-unused-vars */
import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { AxiosError } from 'axios';
import { SyncService } from '@/service';
import { OfflineQueueHelper } from '@/library';
import { AppSettingsDB, OfflineQueueDB } from '@/db/repositories';
import { postSaveTourBagMstrEntity } from '@/api/endpoints';
import { onlineStatus, parseError } from '@/utils';
import ConflictResolution from '@/components/ConflictResolution';
import styles from './OfflineQueueGuard.module.scss';
import OfflineModal from '@/common/OfflineModal';
import { OfflineQueueModel } from '@/db/models';

interface IProcessQueueProps {
  setIsProcessing: (flag: boolean) => void;
  setShowOfflineModal: (flag: boolean) => void;
}

const processQueue = async ({ setIsProcessing, setShowOfflineModal }: IProcessQueueProps) => {
  let showSyncedConfirmation = false;

  try {
    let queue = await OfflineQueueDB.list();
    const pendingItems = queue.filter((entry) => !entry.hasConflict && !entry.error);
    const conflictItems = queue.filter((entry) => entry.hasConflict);

    //if there are pending items to save, and no existing conflicts then flag the offline synced confirmation popup to be shown
    if (pendingItems.length > 0 && conflictItems.length == 0) {
      showSyncedConfirmation = true;
    }
    if (pendingItems.length > 0) {
      setIsProcessing(true);

      for (const entry of pendingItems) {
        const { type, action, payload, id } = entry;

        try {
          if (type === 'TOUR_BAG') {
            const lastSyncDateEntry = await AppSettingsDB.get('lastSyncDate');
            const lastSyncDate = lastSyncDateEntry?.value?.toString() || new Date().toString();
            let tourBagMstrData = payload;
            if (action === 'CREATE') {
              tourBagMstrData = OfflineQueueHelper.removeTourBagInternalId(payload);
            }
            const response = await postSaveTourBagMstrEntity({ tourBagMstrData, lastSyncDate });
            const responseData = response.data as any;

            if (responseData?.status === 'error') {
              throw new Error(responseData.message || 'Server error encountered during sync');
            }
            await OfflineQueueDB.delete(id!);
          }
        } catch (err) {
          console.error(`Error processing queue item ${id}`, err);
          //there was a conflict, so cancel the offline synced confirmation popup. The Conflict Resolution page will be shown instead
          showSyncedConfirmation = false;
          let code = 0;
          const errorMessage = parseError(err);
          let errorPayload: any = null;
          const isConflict = (err as AxiosError).code === '409' || errorMessage.toLowerCase().includes('conflict');
          if (isConflict) {
            //capture tourbag detail data for conflict errors
            const headTypeId = entry.payload.headTypeId ? entry.payload.headTypeId : 0;
            if (err instanceof AxiosError && err.response) {
              switch (headTypeId) {
                case 0: //bsg
                  errorPayload = err.response.data.bsgData[0];
                  break;
                case 1: //driver, fairway, hybrid, putter
                case 2:
                case 3:
                case 5:
                case 6:
                  errorPayload = err.response.data.detailData[0];
                  break;
                case 4:
                case 7: //iron, utility
                  if (err.response.data.ironDetailData) {
                    errorPayload = err.response.data.ironDetailData[0];
                  } else {
                    errorPayload = err.response.data.ironSetData[0];
                  }
                  break;
              }
            }
          } else {
            //error is not conflict related, so just store the status code
            code = (err as any).status;
          }
          // Update retry counter and save operation error
          await OfflineQueueDB.update({
            ...entry,
            retryCount: entry.retryCount + 1,
            hasConflict: isConflict,
            error: {
              error: isConflict ? 'ConflictError' : 'SyncError',
              code: code,
              timestamp: Date.now(),
              message: errorMessage,
              payload: isConflict ? errorPayload || entry.payload : errorPayload || entry.payload,
            },
          });
        }
      }
      //if all pending items were save, and there are no conflicts, show the offline confirmation popup
      if (showSyncedConfirmation) {
        setShowOfflineModal(true);
      }
    }
  } catch (outerErr) {
    // catch unexpected errors to avoid blocking future runs
    console.error('Unexpected error in processQueue:', outerErr);
  } finally {
    setIsProcessing(false);
  }
};

export default function OfflineQueueGuard() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConflictPage, setShowConflictPage] = useState(false);
  const [showOfflineModal, setShowOfflineModal] = useState(false);
  const [spinnerForCompletion, setSpinnerForCompletion] = useState(false);
  const [offlineQueue, setOfflineQueue] = useState<OfflineQueueModel[]>([]);

  const runQueue = async () => {
    if (!onlineStatus.get() || isProcessing) return;

    // TODO: For conflict page add setShowConflict to props
    await processQueue({ setIsProcessing, setShowOfflineModal });
  };

  const onComplete = async () => {
    setShowOfflineModal(false);
    setSpinnerForCompletion(true);
    await SyncService.runAllSyncs();
    const now = new Date();
    await AppSettingsDB.set('lastSyncDate', now.toISOString());
    window.location.reload();
  };

  useEffect(() => {
    // Run on mount
    runQueue();

    const handleReconnect = async () => {
      await runQueue();

      //refresh the queue with latest updates and see if there are any conflicts
      let queue = await OfflineQueueDB.list();
      queue = queue.filter((entry) => entry.hasConflict);
      setOfflineQueue(queue);
      if (queue.length > 0) {
        setShowConflictPage(true);
      }
    };

    window.addEventListener('online', handleReconnect);
    return () => {
      window.removeEventListener('online', handleReconnect);
    };
  }, []);

  return (
    <>
      {/* this renders the matched child route (e.g., <TourBag />) */}
      {!showConflictPage && <Outlet />}
      {showConflictPage && <ConflictResolution offlineQueue={offlineQueue} />}
      <OfflineModal onCancel={() => {}} onClose={onComplete} isOpen={showOfflineModal} modalType={'Synced'} />

      {/* if queue is being flushed prevent user action */}
      {/* TODO: for conlfict page check: {isProcessing && !showConflictPage && ( */}
      {isProcessing && !showConflictPage && (
        <div className={styles.backdrop}>
          <div className={styles.content}>
            <div className={styles.row}>
              <div className={styles.spinner} />
              Syncing offline data...
            </div>
          </div>
        </div>
      )}
      {spinnerForCompletion && (
        <div className={styles.backdrop}>
          <div className={styles.content}>
            <div className={styles.row}>
              <div className={styles.spinner} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
